import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsOfServicePage: React.FC = () => {
  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);

    // Obfuscate email address - gets assembled after page load to avoid bot detection
    const emailElement = document.getElementById('terms-email-link');
    if (emailElement) {
      const parts = ['info', '@', 'ocean', 'bio', 'metrics', '.', 'com'];
      const email = parts.join('');
      emailElement.textContent = email;
    }
  }, []);

  return (
    <div className="d-flex flex-column min-vh-100">
      <Header isMainPage={false} />
      <main className="flex-grow-1 mt-5 pt-5">
        <div className="container py-4">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-8">
              <h1 className="display-4 mb-4 text-center" style={{ color: '#00f5ff' }}>Terms of Service</h1>

              <p className="mb-4 text-center"><strong>Effective Date:</strong> September 12, 2025</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>1. Introduction and Acceptance</h2>
              <p className="mb-3">
                These Terms of Service ("Terms") govern your use of the Ocean BioMetrics website at www.oceanbiometrics.com (the "Website") and outline the general terms for engaging Ocean BioMetrics Ltd ("Ocean BioMetrics," "we," "us," or "our") for professional consulting services in environmental microbial ecology, bioinformatics, and ocean intelligence.
              </p>
              <p className="mb-4">
                By using our Website or engaging our services, you agree to be bound by these Terms. If you are accepting on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.
              </p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>2. Our Services</h2>
              <p className="mb-3">Ocean BioMetrics provides specialized consulting services in:</p>
              <ul className="mb-3">
                <li><strong>Genomic and Metagenomic Analysis:</strong> Environmental DNA analysis, microbial genome reconstruction, and biodiversity assessments</li>
                <li><strong>Bioinformatics Solutions:</strong> Custom analytical workflows, data processing pipelines, and computational biology services</li>
                <li><strong>Ocean Intelligence:</strong> Integration of multi-omics data with environmental parameters for marine ecosystem insights</li>
                <li><strong>Strategic Advisory:</strong> Blue finance mechanisms, conservation strategies, and biodiversity valuation</li>
                <li><strong>Knowledge Systems:</strong> Development of biodiversity knowledge graphs and ecological network analyses</li>
              </ul>
              <p className="mb-4">Specific project scope, deliverables, and terms are defined in separate Statement of Work (SOW) documents for each engagement.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>3. Website Use</h2>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Acceptable Use</h3>
              <p className="mb-2">You may use our Website for lawful purposes only. You agree not to:</p>
              <ul className="mb-3">
                <li>Attempt to gain unauthorized access to any portion of the Website</li>
                <li>Use the Website to transmit harmful code or malware</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon any third-party rights</li>
              </ul>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Intellectual Property</h3>
              <p className="mb-4">All content on our Website, including text, graphics, logos, and software, is the property of Ocean BioMetrics or its licensors and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>4. Consulting Engagements</h2>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Project Initiation</h3>
              <p className="mb-2">Professional services begin upon mutual execution of a Statement of Work (SOW) or similar agreement that specifies:</p>
              <ul className="mb-3">
                <li>Project scope and objectives</li>
                <li>Deliverables and milestones</li>
                <li>Timeline and deadlines</li>
                <li>Fees and payment terms</li>
                <li>Specific terms applicable to the project</li>
              </ul>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Client Responsibilities</h3>
              <p className="mb-2">Clients agree to:</p>
              <ul className="mb-4">
                <li>Provide timely access to necessary data, information, and resources</li>
                <li>Designate authorized representatives for project communication</li>
                <li>Review and provide feedback on deliverables within agreed timeframes</li>
                <li>Ensure all provided data complies with applicable laws and regulations</li>
              </ul>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>5. Confidentiality</h2>
              <p className="mb-2">Both parties acknowledge that they may have access to confidential information during the engagement. Each party agrees to:</p>
              <ul className="mb-2">
                <li>Maintain the confidentiality of all proprietary information received</li>
                <li>Use confidential information solely for the purposes of the engagement</li>
                <li>Not disclose confidential information to third parties without written consent</li>
                <li>Return or destroy confidential information upon request or termination of services</li>
              </ul>
              <p className="mb-4">This obligation does not apply to information that is publicly available, independently developed, or required to be disclosed by law.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>6. Data Handling and Security</h2>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Client Data</h3>
              <ul className="mb-3">
                <li>Clients retain all rights to their data and samples</li>
                <li>We process client data solely for the agreed project purposes</li>
                <li>We implement industry-standard security measures to protect client data</li>
                <li>Upon project completion, we return or securely delete client data as agreed</li>
              </ul>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Compliance</h3>
              <p className="mb-2">Clients must ensure that any biological samples, genomic data, or environmental data provided to us:</p>
              <ul className="mb-4">
                <li>Are collected and shared in compliance with all applicable laws</li>
                <li>Have necessary permits and authorizations (e.g., Nagoya Protocol compliance)</li>
                <li>Do not contain human health information unless specifically agreed and properly authorized</li>
              </ul>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>7. Intellectual Property Rights</h2>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Pre-existing IP</h3>
              <p className="mb-3">Each party retains ownership of its pre-existing intellectual property. Ocean BioMetrics retains all rights to its proprietary methods, workflows, algorithms, and general know-how.</p>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Project Deliverables</h3>
              <p className="mb-2">Unless otherwise specified in the SOW:</p>
              <ul className="mb-3">
                <li>Clients own the specific results and deliverables created for their project</li>
                <li>Ocean BioMetrics retains the right to use general methodologies and non-confidential learnings</li>
                <li>Any custom software or tools developed remain the property of Ocean BioMetrics unless otherwise agreed</li>
              </ul>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Publications and Attribution</h3>
              <p className="mb-4">Any publication or public disclosure of project results requires mutual agreement on attribution and acknowledgment.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>8. Fees and Payment</h2>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Payment Terms</h3>
              <ul className="mb-3">
                <li>Fees are specified in the SOW for each engagement</li>
                <li>Invoices are issued according to the payment schedule in the SOW</li>
                <li>Payment is due within 30 days of invoice date unless otherwise specified</li>
                <li>Late payments may incur interest at 1.5% per month or the maximum legal rate</li>
              </ul>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Expenses</h3>
              <p className="mb-4">Reasonable travel and other project-related expenses will be billed separately with prior client approval.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>9. Warranties and Disclaimers</h2>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Our Commitment</h3>
              <p className="mb-3">We warrant that our services will be performed in a professional manner consistent with industry standards and that we have the necessary expertise and resources to perform the agreed services.</p>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Limitations</h3>
              <p className="mb-2"><strong>EXCEPT AS EXPRESSLY PROVIDED, OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT GUARANTEE SPECIFIC RESEARCH OUTCOMES OR RESULTS.</strong></p>
              <p className="mb-2">Ocean BioMetrics does not warrant that:</p>
              <ul className="mb-4">
                <li>Analysis results will meet all client objectives</li>
                <li>Biological or environmental insights will lead to specific commercial outcomes</li>
                <li>Third-party data or tools used in analysis are error-free</li>
              </ul>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>10. Limitation of Liability</h2>
              <p className="mb-2"><strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong></p>
              <ul className="mb-4">
                <li>Our total liability for any claim arising from our services shall not exceed the fees paid for the specific project giving rise to the claim</li>
                <li>Neither party shall be liable for indirect, incidental, consequential, or punitive damages</li>
                <li>These limitations apply regardless of the legal theory under which liability is asserted</li>
              </ul>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>11. Indemnification</h2>
              <p className="mb-2">Each party agrees to indemnify and hold harmless the other party from any claims, damages, or expenses arising from:</p>
              <ul className="mb-4">
                <li>Breach of these Terms or any SOW</li>
                <li>Violation of applicable laws or regulations</li>
                <li>Infringement of third-party rights</li>
                <li>Gross negligence or willful misconduct</li>
              </ul>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>12. Term and Termination</h2>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Website Terms</h3>
              <p className="mb-3">These Terms remain in effect as long as you use our Website.</p>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Service Engagements</h3>
              <ul className="mb-4">
                <li>Project terms are specified in each SOW</li>
                <li>Either party may terminate for material breach with 30 days written notice and opportunity to cure</li>
                <li>Upon termination, all fees for completed work become immediately due</li>
                <li>Termination does not affect accrued rights or obligations</li>
              </ul>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>13. Force Majeure</h2>
              <p className="mb-4">Neither party shall be liable for delays or failures in performance due to circumstances beyond their reasonable control, including but not limited to acts of nature, government actions, labor disputes, or infrastructure failures.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>14. General Provisions</h2>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Governing Law</h3>
              <p className="mb-3">These Terms are governed by the laws of England and Wales, without regard to conflict of law principles.</p>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Dispute Resolution</h3>
              <p className="mb-3">The parties agree to attempt good faith resolution of disputes through negotiation before pursuing legal remedies.</p>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Entire Agreement</h3>
              <p className="mb-3">These Terms, together with any executed SOW and our Privacy Policy, constitute the entire agreement between the parties.</p>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Amendments</h3>
              <p className="mb-3">We may update these Terms from time to time. Material changes will be communicated to active clients.</p>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Severability</h3>
              <p className="mb-3">If any provision is found unenforceable, the remaining provisions shall continue in effect.</p>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>Assignment</h3>
              <p className="mb-4">Neither party may assign their rights or obligations without the other party's written consent.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>15. Contact Information</h2>
              <p className="mb-2">For questions about these Terms or to engage our services, please contact us:</p>
              <p className="mb-1"><strong>Email:</strong> <button id="terms-email-link" className="text-decoration-none hover-primary border-0 bg-transparent p-0" style={{ cursor: 'pointer', color: 'inherit' }} onClick={() => { const parts = ['info', '@', 'ocean', 'bio', 'metrics', '.', 'com']; const email = parts.join(''); window.location.href = `mailto:${email}`; }}>Loading...</button></p>
              <p className="mb-1"><strong>Website:</strong> www.oceanbiometrics.com</p>
              <p className="mb-0">Ocean BioMetrics Ltd<br /></p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfServicePage;